import { Component, OnInit } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Router } from '@angular/router';
import { AuthService, LoginResponse } from '../../core/services/auth';
import { NetworkStatusService } from '../../core/services/network-status';
import { UserService } from '../../core/services/user.service';
import { LanguageService } from '../../core/services/language.service';
import { SubscriberService } from '../../core/services/subscriber.service';
import { EncryptionService } from '../../core/services/encryption';
import { SETTINGS } from '../../core/config/common.settings';
import { environment } from '../../../environments/environment';

interface ErrorMapping {
  [key: string]: string;
}

// Extend the LoginResponse interface to include accessToken
interface ExtendedLoginResponse extends LoginResponse {
  accessToken?: string;
}

@Component({
  selector: 'app-login',
  imports: [
    InputGroupModule, 
    InputGroupAddonModule, 
    FormsModule, 
    InputTextModule, 
    CommonModule, 
    ReactiveFormsModule, 
    ButtonModule, 
    PasswordModule,
    ToastModule,
    ProgressSpinnerModule
  ],
  providers: [MessageService],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login implements OnInit {
  loading: boolean = false;
  checkingSession: boolean = true; // Loading state for session validation
  isOnline: boolean = true;
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  private readonly errorMessages: ErrorMapping = {
    'user not found': 'User not found. Please check your email address.',
    'invalid credentials': 'Invalid credentials. Please check your email and password.',
    'user disabled': 'Your account has been disabled. Please contact support.',
    '0 unknown error': 'Unable to connect to the server. Please check your internet connection and try again.',
    'network error': 'Network error. Please check your internet connection and try again.',
    'timeout': 'Request timed out. Please try again.',
  };

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router,
    private networkStatusService: NetworkStatusService,
    private userService: UserService,
    private languageService: LanguageService,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService
  ) {}

  ngOnInit() {
    // Load saved language preference and apply it if different from current URL
    this.loadAndApplySavedLanguagePreference();
    
    this.networkStatusService.getNetworkStatus().subscribe(status => {
      this.isOnline = status;
      console.log('Network Status:', status ? 'Online' : 'Offline');
      if (!status) {
        this.showMessage('error', 'Offline', 'You are offline. Please check your internet connection.', 5000);
      }
    });

    // Check if user already has a valid session and redirect accordingly
    this.checkExistingSession();
  }

  /**
   * Get language from current URL path
   */
  private getLanguageFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/^\/(en|de|ro)(\/|$)/);
    return match ? match[1] : null;
  }

  /**
   * Load saved language preference and redirect if needed
   */
  private loadAndApplySavedLanguagePreference(): void {
    // Only apply language redirects in production/deployment
    if (!this.languageService.isLanguageFeatureEnabled()) {
      return;
    }

    const savedLanguage = localStorage.getItem('preferredLanguage');
    const currentUrlLang = this.getLanguageFromUrl();
    
    // Only apply if:
    // 1. There's a saved language preference
    // 2. It's a valid language
    // 3. Current URL language doesn't match saved preference
    if (savedLanguage && 
        this.languageService.isValidLanguage(savedLanguage) && 
        currentUrlLang !== savedLanguage) {
      // Redirect to the saved language path
      const currentPath = window.location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(en|de|ro)(\/|$)/, '/');
      const newPath = `/${savedLanguage}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
      
      // Only redirect if paths are different
      if (window.location.pathname !== newPath) {
        window.location.href = newPath;
      }
    }
  }

  /**
   * Check if user has an existing valid session and redirect to appropriate page
   */
  private checkExistingSession(): void {
    this.checkingSession = true;
    this.authService.checkSessionAndRoute(this.router, this.userService).subscribe({
      next: (wasRouted: boolean) => {
        this.checkingSession = false;
        if (wasRouted) {
          console.log('Login: User has valid session, redirected to appropriate page');
        } else {
          console.log('Login: No valid session found, user can proceed with login');
        }
      },
      error: (error) => {
        this.checkingSession = false;
        console.error('Login: Error checking existing session:', error);
        // Don't show error to user, just allow them to login normally
      }
    });
  }

  async executeRecaptcha(): Promise<string | null> {
    return new Promise((resolve) => {
      if (typeof (window as any).grecaptcha === 'undefined') {
        // Wait for reCAPTCHA script to load
        const checkRecaptcha = setInterval(() => {
          if (typeof (window as any).grecaptcha !== 'undefined') {
            clearInterval(checkRecaptcha);
            this.getRecaptchaToken(resolve);
          }
        }, 100);
      } else {
        this.getRecaptchaToken(resolve);
      }
    });
  }

  private getRecaptchaToken(resolve: (token: string | null) => void) {
    if (typeof (window as any).grecaptcha !== 'undefined') {
      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha
          .execute(this.recaptchaSiteKey, { action: 'login' })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            console.error('reCAPTCHA v3 error:', error);
            resolve(null);
          });
      });
    } else {
      resolve(null);
    }
  }

  async onSubmit() {
    if (!this.loginForm.valid) {
      this.showMessage('warn', 'Validation Error', 'Please fill in all required fields correctly.', 4000);
      return;
    }

    this.loading = true;
    
    // Execute reCAPTCHA v3
    const recaptchaToken = await this.executeRecaptcha();
    
    if (!recaptchaToken) {
      this.loading = false;
      this.showMessage('error', 'Verification Failed', 'reCAPTCHA verification failed. Please try again.', 4000);
      return;
    }

    const { email, password } = this.loginForm.value;
    
    // Ensure email and password are valid strings
    if (!email || !password || typeof email !== 'string' || typeof password !== 'string') {
      this.loading = false;
      this.showMessage('error', 'Validation Error', 'Please provide valid email and password.', 4000);
      return;
    }
    
    // Convert email to lowercase for case-insensitive login
    const normalizedEmail = email.toLowerCase().trim();
    
    this.authService.postLogin({ username: normalizedEmail, password, recaptchaToken }).subscribe({
      next: (response: LoginResponse) => this.handleLoginSuccess(response, normalizedEmail),
      error: (error) => {
        this.handleError(error, normalizedEmail);
      }
    });
  }

  private handleLoginSuccess(response: ExtendedLoginResponse, email: string) {
    this.loading = false;
    
    if (response.requiresOTP) {
      this.showMessage('success', 'Verification Required', response.message, 4000);
      
      // Store userId in session storage for OTP verification
      if (response.userId) {
        sessionStorage.setItem('loginUserId', response.userId);
        sessionStorage.setItem('loginEmail', email);
      }
      
      setTimeout(() => {
        this.router.navigate(['/two-factor-auth']);
      }, 100);
    } else {
      // Handle case where OTP is not required (SUPER_DEV_ADMIN or other direct login)
      this.showMessage('success', 'Success', response.message, 3000);
      
      // If user object is provided (SUPER_DEV_ADMIN), set the session
      if (response.user) {
        // Load and apply language preference before setting session
        this.loadAndApplyLanguagePreference(response.user);
        
        this.authService.setDirectLoginSession(response.user, response.accessToken);
        
        // Check if user needs to be redirected to account setup
        if (response.user?.isFirstLogin) {
          // User is first login and status is pending - redirect to account setup
          setTimeout(() => {
            this.router.navigate(['/user-account-setup']);
          }, 0);
        } else if (response.user?.isFirstLogin && response.user?.accountStatus === 'PENDING') {
          // User has already filled the form but status is still pending - redirect to account setup
          setTimeout(() => {
            this.router.navigate(['/user-account-setup']);
          },  0);
        } else {
          // User has completed first login or doesn't need account setup - redirect to appropriate dashboard
          setTimeout(() => {
            this.router.navigate([this.getDashboardRouteByRole(response.user?.role?.identification)]);
          }, 0);
        }
      } else {
        // Fallback navigation - redirect to admin dashboard for non-FSR users
        setTimeout(() => {
          this.router.navigate(['/admin/user-management']);
        }, 0);
      }
    }
  }

  private handleError(error: any, email: string) {
    this.loading = false;
    
    // Add null check for error parameter
    if (!error) {
      this.showMessage('error', 'Login Failed', 'An unexpected error occurred. Please try again.', 5000);
      return;
    }
    
    const errorMessage = this.extractErrorMessage(error);
    console.log('Extracted error message:', errorMessage);
    
    this.showMessage('error', 'Login Failed', errorMessage, 5000);
  }

  private extractErrorMessage(error: any): string {
    // Ensure we have a valid message string
    const message = error?.error?.message || error?.message || 'Login failed. Please try again.';
    
    // Add null check before calling toLowerCase()
    if (!message || typeof message !== 'string') {
      return 'Login failed. Please try again.';
    }
    
    const lowerMessage = message.toLowerCase();
    
    // Handle server down/network errors specifically
    if (error?.status === 0 || lowerMessage.includes('0 unknown error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (error?.status >= 500) {
      return 'Server error. Please try again later.';
    }
    
    // Check for specific error patterns
    for (const [pattern, userMessage] of Object.entries(this.errorMessages)) {
      if (lowerMessage.includes(pattern)) {
        return userMessage;
      }
    }
    
    return message;
  }

  private showMessage(severity: string, summary: string, detail: string, life?: number) {
    // Ensure all parameters are valid strings
    if (!severity || !summary || !detail || typeof severity !== 'string' || typeof summary !== 'string' || typeof detail !== 'string') {
      console.error('Invalid parameters passed to showMessage:', { severity, summary, detail, life });
      return;
    }
    
    this.messageService.add({ 
      severity, 
      summary, 
      detail,
      life: life || 3000
    });
  }

  onForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  /**
   * Get the appropriate dashboard route based on user role
   * @param roleIdentification - The role identification string
   * @returns The dashboard route path (without language prefix - Angular Router handles baseHref)
   */
  private getDashboardRouteByRole(roleIdentification?: string): string {
    switch (roleIdentification) {
      case 'FSR':
        return '/admin/fsr-dashboard';
      case 'SA':
      case 'A':
        return '/admin/admin-dashboard';
      case 'CSM':
      case 'SM':
        return '/admin/user-management';
      default:
        // Default fallback for unknown roles or undefined
        return '/admin/user-management';
    }
  }

  private loadAndApplyLanguagePreference(user: any) {
    // Priority: 1. User language preference, 2. Subscription language, 3. Default (en)
    let languageToApply = 'en';
    
    // Check user language preference first
    if (user?.language && this.languageService.isValidLanguage(user.language)) {
      languageToApply = user.language;
      // Apply the language immediately
      this.languageService.setLanguage(languageToApply);
      localStorage.setItem('preferredLanguage', languageToApply);
      return;
    }
    
    // Fallback to subscription language - get subscriberId from user object
    const subscriberId = user?.subscriberId?._id || user?.subscriberId;
    
    if (subscriberId) {
      this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
        const actualSubscriber = subscriber?.subscriber || subscriber;
        if (actualSubscriber?.language && this.languageService.isValidLanguage(actualSubscriber.language)) {
          languageToApply = actualSubscriber.language;
        }
        // Apply the language
        this.languageService.setLanguage(languageToApply);
        localStorage.setItem('preferredLanguage', languageToApply);
      }).catch((error) => {
        console.error('Error loading subscriber language:', error);
        // Use default
        this.languageService.setLanguage(languageToApply);
        localStorage.setItem('preferredLanguage', languageToApply);
      });
    } else {
      // No subscriber ID available, use default
      this.languageService.setLanguage(languageToApply);
      localStorage.setItem('preferredLanguage', languageToApply);
    }
  }
}
